<script setup>
	import { ref, computed, nextTick } from 'vue';
	import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue';
	import { Navigation, Pagination, Keyboard, A11y } from 'swiper';
	import 'swiper/scss';
	import { DsIconButton, DsText, DsBadge } from '@coloplast/design-system';
	import StaticWidthCard from 'shared/components/contentElements/staticWidthCard.vue';
	import VideoPopup from 'shared/components/VideoElements/videoPopup.vue';

	const props = defineProps({
		vm: {
			type: Object,
			default: () => {},
		},
	});

	const videoUrl = ref(null);
    const videoIsPlaying = ref(false);

    function setVideoUrl(value) {
        videoUrl.value = value;
        videoIsPlaying.value = true;
    }

    function closePopup() {
        videoUrl.value = null;
        videoIsPlaying.value = false;
    }

	const swiper = ref(null);
	const settings = ref({
		speed: 500,
		mousewheel: true,
		keyboard: true,
		watchOverflow: true,
		breakpoints: {
			1200: {
				slidesPerView: 4,
				slidesPerGroup: 4,
				spaceBetween: 24,
			},
            992: {
                slidesPerView: 3.1,
                slidesPerGroup: 3,
                spaceBetween: 24,
            },
			576: {
				slidesPerView: 2.05,
				slidesPerGroup: 2,
				spaceBetween: 24,
			},
			0: {
				slidesPerView: 1.01,
				slidesPerGroup: 1,
				spaceBetween: 12,
				sliderOffsetAfter: 24,
			},
		},
		navigation: {
			nextEl: `.next-${props.vm.BaseViewModel.CurrentBlockId}`,
			prevEl: `.prev-${props.vm.BaseViewModel.CurrentBlockId}`,
		},
		pagination: {
			el: `.pagination-${props.vm.BaseViewModel.CurrentBlockId}`,
			type: 'fraction',
			clickable: false,
		},
		modules: [Navigation, Pagination, Keyboard, A11y],
	});
    const slidesPerView = ref(0);

    async function initCarousel(swiper) {
        await nextTick();
        swiper.value = swiper;
        slidesPerView.value = swiper?.params?.slidesPerView;
    }

    function onResize(swiper) {
        slidesPerView.value = swiper?.params?.slidesPerView;
        swiper.value.slideTo(0);
    }

	const showNavigation = computed(() => {
		return Math.ceil(currentTabItemCount.value / slidesPerView.value) > 1;
	});

    const tab = ref(!props.vm.BaseViewModel.HideLearningTab ? props.vm.CurrentBlock.LearningTabLabel : props.vm.CurrentBlock.ProductsTabLabel);

    const currentTab = computed({
        get() {
            return tab.value;
        },
        set(newTabValue) {
            tab.value = newTabValue;
        },
    });

	const currentTabItems = computed(() => {
		if (tab.value && currentTab.value === props.vm.CurrentBlock.LearningTabLabel) {
			return props.vm.BaseViewModel.RelatedContent;
		} else {
            return props.vm.BaseViewModel.RelatedProducts;
        }
	});

	const currentTabItemCount = computed(() => {
        if (currentTab.value === props.vm.CurrentBlock.LearningTabLabel) {
            return props.vm.BaseViewModel.LearningTabItemsCount;
        } else {
            return props.vm.BaseViewModel.ProductsTabItemsCount;
        }
	});

	const isTabNavigationVisible = computed(() => {
		return !(props.vm.BaseViewModel.HideLearningTab || props.vm.BaseViewModel.HideProductsTab);
	});
</script>

<template>
	<div class="c-content-library-carousel__container">
        <DsText
            v-if="vm.CurrentBlock.Headline"
            html-element="h3"
            text-type="heading-2xl"
            class="c-content-library-carousel__headline c-content-library-carousel__headline--related"
        >
            {{ vm.CurrentBlock.Headline }}
        </DsText>
        <div class="c-content-library-carousel__toparea c-content-library-carousel__toparea--related">
            <nav v-if="isTabNavigationVisible">
                <ul class="c-content-library-carousel__tab-list">
                    <li
                        v-if="!vm.BaseViewModel.HideLearningTab && vm.BaseViewModel.LearningTabItemsCount > 0"
                        class="c-content-library-carousel__tab-item"
                        :class="{ 'c-content-library-carousel__tab-item--active': currentTab === vm.CurrentBlock.LearningTabLabel }"
                        data-testid="learning-tab"
                        @click="currentTab = vm.CurrentBlock.LearningTabLabel"
                    >
                        <DsText
                            html-element="span"
                            text-style="medium"
                        >
                            {{ vm.CurrentBlock.LearningTabLabel }}
                        </DsText>
                        <DsBadge :text="vm.BaseViewModel.LearningTabItemsCount"></DsBadge>
                    </li>
                    <li
                        v-if="!vm.BaseViewModel.HideProductsTab && vm.BaseViewModel.ProductsTabItemsCount > 0"
                        class="c-content-library-carousel__tab-item"
                        :class="{ 'c-content-library-carousel__tab-item--active': currentTab === vm.CurrentBlock.ProductsTabLabel }"
                        data-testid="products-tab"
                        @click="currentTab = vm.CurrentBlock.ProductsTabLabel"
                    >
                        <DsText
                            html-element="span"
                            text-style="medium"
                        >
                            {{ vm.CurrentBlock.ProductsTabLabel }}
                        </DsText>
                        <DsBadge :text="vm.BaseViewModel.ProductsTabItemsCount"></DsBadge>
                    </li>
                </ul>
            </nav>
            <div
                class="c-content-library-carousel__nav"
                :class="{ '--hidden': !showNavigation, 'c-content-library-carousel__nav--no-tabs' : !isTabNavigationVisible }"
            >
                <DsIconButton
                    aria-label="Previous"
                    icon-name="chevron-left"
                    type="button"
                    role="button"
                    size="sm"
                    :class="`prev-${vm.BaseViewModel.CurrentBlockId}`"
                >
                </DsIconButton>
                <span
                    class="c-content-library-carousel__nav-pagination"
                    :class="`pagination-${vm.BaseViewModel.CurrentBlockId}`"
                >
            </span>
                <DsIconButton
                    aria-label="Next"
                    icon-name="chevron-right"
                    type="button"
                    role="button"
                    size="sm"
                    :class="`next-${vm.BaseViewModel.CurrentBlockId}`"
                >
                </DsIconButton>
            </div>
        </div>
		<Swiper
			v-bind="settings"
			@init="initCarousel"
			@resize="onResize"
		>
			<SwiperSlide
				v-for="item in currentTabItems"
				:key="item.Id"
			>
				<StaticWidthCard
					:item="item"
                    is-related-content-item
					:is-learning-item="currentTab && currentTab === props.vm.CurrentBlock.LearningTabLabel"
                    :is-product-item="currentTab === props.vm.CurrentBlock.ProductsTabLabel"
					@clicked="setVideoUrl"
				/>
			</SwiperSlide>
		</Swiper>
		<VideoPopup
			v-if="videoIsPlaying && videoUrl"
			:video-url="videoUrl"
			@close-popup="closePopup"
		/>
	</div>
</template>
